import { useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  TextField,
  Typography,
  useDeviceType,
  AccordionDetails,
  AccordionSummary,
  Notification,
  Grid,
  Accordion,
} from '@packages/shared';
import { ArrowMiniDown32 } from '@packages/themes/icons';
import type { CustomerDataWithStatus } from '@packages/utilities';
import { useCustomer } from '@packages/utilities';
import type { GTMEventGlycerinDisplayNewsletterConfirm } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { Language } from '@packages/config';
import { useRouter } from 'next/router';
import config from '@packages/config';
import { ActivityOverlay } from '../ActivityOverlay';
import { basicContainer } from '../styles';
import { InputIDs } from '../types';
import { subscribeSheetMessages } from './helpers/messages';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { submitMessage } from '../GeneralForm/helpers/handleSubmit';
import { formMessages } from '../messages';

type InputId = 'email';
type FormValues = {
  [InputIDs.EMAIL]: string;
};
type SetErrorType = `root.${InputId}` | 'root';

const newsletterSubscribeFormSchema = (intl: IntlShape) =>
  yup.object().shape({
    [InputIDs.EMAIL]: yup
      .string()
      .email(intl.formatMessage(formMessages[InputIDs.EMAIL].error.valid))
      .required(intl.formatMessage(formMessages[InputIDs.EMAIL].error)),
  });

const TermsAccordion = () => {
  const intl = useIntl();
  const { locale, defaultLocale = 'de' } = useRouter();
  const { nlTexts } = useNlSheetTexts();
  const {
    host: { domain },
    forms: { newsletterSubscribe },
    i18n: { languages },
  } = config;
  const configLanguage = (locale && locale in languages ? locale : defaultLocale) as Language;

  return (
    <Accordion
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        typography: 'body2',
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowMiniDown32 />}
        aria-controls="newslettersheet-terms"
        id="newslettersheet-terms"
        sx={{
          padding: 0,
          justifyContent: 'left',
          '&.Mui-expanded': { minHeight: 'unset' },
          '.MuiAccordionSummary-content': { flexGrow: 'unset', margin: 0 },
        }}
      >
        {(nlTexts && nlTexts.field_voucher_conditions_id?.label) ||
          intl.formatMessage(subscribeSheetMessages.termsHeadline)}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}>
          {nlTexts && nlTexts.field_voucher_conditions_id?.field_conditions?.processed ? (
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: nlTexts.field_voucher_conditions_id?.field_conditions.processed,
              }}
            />
          ) : (
            <FormattedMessage
              id={subscribeSheetMessages.termsContent.id}
              defaultMessage={subscribeSheetMessages.termsContent.defaultMessage}
              values={{
                aDomain: (chunks) => <a href={`https://${domain}`}>{chunks}</a>,
                aVoucher: (chunks) => (
                  <a href={newsletterSubscribe[configLanguage].voucherLink}>{chunks}</a>
                ),
              }}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.EMAIL}`]: customer?.email || '',
});
/**
 * A compact Newsletter Subscribe Form with a simple success page after successful submit
 */
const Page1Form = ({ onSuccess }: { onSuccess: () => void }) => {
  const intl = useIntl();
  const { locale, defaultLocale = 'de' } = useRouter();
  const { nlTexts } = useNlSheetTexts();
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const [notification, setNotification] = useState({ show: false, message: '' });
  const {
    forms: { newsletterSubscribe },
    i18n: { languages },
  } = config;
  const scheme = newsletterSubscribeFormSchema(intl);
  const { handleSubmit, register, formState, setError, getValues } = useForm<FormValues>({
    resolver: yupResolver(scheme),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const { errors, isValid, isSubmitting } = formState;
  const onSubmit = (data: FormValues) =>
    submitMessage<SetErrorType>({
      endpoint: 'subscribe',
      intl,
      isMobile,
      onSuccess: () => {
        onSuccess();
        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'subscribe',
        });
        if (getValues('email')) {
          dispatchGTMEvent<GTMEventGlycerinDisplayNewsletterConfirm>({
            event: 'DisplayNewsletterConfirm',
            DisplayNewsletterConfirmData: {
              email: getValues('email'),
              custom: { areaKey: 'slideinPromotionFooter' },
            },
          });
        }
      },
      onError: (message) => setNotification({ message, show: true }),
      setError,
      convertedFormNLData: {
        emailAddress: data.email,
        areaKey: 'slideinPromotionFooter',
      },
    });

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: InputId) => ({
    ...register(inputId),
    id: inputId,
    helperText: errors[inputId]?.message,
    error: !!errors[inputId],
  });

  const buttonText =
    (nlTexts && (isMobile ? nlTexts.field_button_label_mobile : nlTexts.field_button_label)) ||
    intl.formatMessage(
      isMobile ? subscribeSheetMessages.buttonMobile : subscribeSheetMessages.button,
    );

  const configLanguage = (locale && locale in languages ? locale : defaultLocale) as Language;
  const unsubscribeLink = newsletterSubscribe[configLanguage]?.unsubscribeLink;

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Notification
        open={notification.show}
        severity="error"
        autoHideDuration={3500}
        onClose={() => setNotification({ ...notification, show: false })}
      >
        {notification.message}
      </Notification>
      <Box sx={{ ...basicContainer, padding: 2 }}>
        <Typography variant="h5" component="p" sx={{ marginBottom: 2.25 }}>
          {(nlTexts && nlTexts.field_headline) ||
            intl.formatMessage(subscribeSheetMessages.formHeadline)}
        </Typography>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container columns={[1, 2]} columnSpacing={1.5}>
            <Grid item xs={1}>
              <TextField
                {...getFieldProps(InputIDs.EMAIL)}
                sx={{ paddingTop: 0 }}
                fullWidth
                placeholder={intl.formatMessage(subscribeSheetMessages.emailPlaceholder)}
              />
            </Grid>
            <Grid item xs={1}>
              <Button
                disabled={!isValid}
                size="large"
                fullWidth
                color="primary"
                type="submit"
                clickTrackingProps={{
                  category: 'newsletter-sheet',
                  label: 'submitbutton',
                }}
              >
                {buttonText}
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1, mb: 2, typography: 'body2', a: { color: 'text.dark' } }}>
            <FormattedMessage
              id={subscribeSheetMessages.formDescription.id}
              defaultMessage={subscribeSheetMessages.formDescription.defaultMessage}
              values={{
                a: (chunks) => <a href={unsubscribeLink}>{chunks}</a>,
              }}
            />
          </Box>

          <TermsAccordion />
        </form>
      </Box>
    </ActivityOverlay>
  );
};

export const NewsletterSubscribeCompact = () => {
  const intl = useIntl();
  const [page, setPage] = useState(1);

  return page === 1 ? (
    <Page1Form onSuccess={() => setPage(2)} />
  ) : (
    <Box sx={{ ...basicContainer, padding: 2 }}>
      <Typography variant="h3" component="p" sx={{ marginBottom: 2.25 }}>
        {intl.formatMessage(subscribeSheetMessages.successHeadline)}
      </Typography>
      <Box sx={{ paddingBottom: 2.25, typography: 'body2' }}>
        {intl.formatMessage(subscribeSheetMessages.successContent)}
      </Box>
    </Box>
  );
};
